import React, { useCallback, useEffect, useState } from "react";

import { ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import type { TPartInteractionWithImage } from "~/database/types";
import { useBatchGeneratePartImages } from "~/hooks/mutations/use-batch-generate-part-images";
import { useBatchGenerateStatus } from "~/hooks/queries/use-batch-generate-status";
import { usePartImagePreferences } from "~/hooks/queries/use-part-image-preferences";
import { logger } from "~/lib/logger";

interface GenerateImagesBannerProps {
    partInteractions: TPartInteractionWithImage[];
    sessionId: string;
    enabled?: boolean;
    clientId?: string;
    onComplete?: () => void;
}

export const GenerateImagesBanner: React.FC<GenerateImagesBannerProps> = ({
    partInteractions,
    sessionId,
    enabled = true,
    clientId,
    onComplete,
}) => {
    const { data: preferences, isLoading: preferencesLoading } =
        usePartImagePreferences();
    const { mutate: batchGenerateImages, isPending: isBatchGenerating } =
        useBatchGeneratePartImages();

    // AIDEV-NOTE: Track generation state and monitor visibility
    const [showMonitor, setShowMonitor] = useState(false);
    const [hasShownCompletionMessage, setHasShownCompletionMessage] =
        useState(false);
    // AIDEV-NOTE: Track lifecycle: queued -> saw active > 0 -> active === 0
    const [hasQueued, setHasQueued] = useState(false);
    // AIDEV-NOTE: Timestamp of the enqueue. React Query can hold a cached
    // zero-count result from BEFORE this batch was queued; counting that as an
    // idle poll would let a single genuine idle response satisfy the two-poll
    // fallback and declare success before Inngest has even registered the jobs.
    // Only responses fetched after this instant are counted.
    const [queuedAt, setQueuedAt] = useState<number | null>(null);
    // AIDEV-NOTE: Set only after a refetch that we KNOW started post-enqueue has
    // resolved. Response age alone is not proof of that ordering: a status request
    // issued before the enqueue but slow to resolve carries a fresh timestamp while
    // describing the old world. Everything from here on is genuinely about this
    // batch.
    const [statusTrusted, setStatusTrusted] = useState(false);
    const [hasSeenActive, setHasSeenActive] = useState(false);

    const [initialPartsNeedingImages, setInitialPartsNeedingImages] = useState<
        string[]
    >([]);

    // AIDEV-NOTE: Use batch status API for monitoring progress
    // AIDEV-NOTE: forceRefetch while a batch is outstanding. Without it the hook
    // stops polling the moment a response reports no active jobs, so a batch that
    // finished between polls returns exactly ONE idle result and the idle-poll
    // counter below can never reach its threshold — the very case this is meant to
    // catch. Polling ends once completion is recorded.
    const {
        data: batchStatus,
        dataUpdatedAt,
        refetch: refetchBatchStatus,
    } = useBatchGenerateStatus({
        clientId: clientId!,
        sessionId,
        enabled: showMonitor && !!clientId,
        refetchIntervalMs: 5000,
        forceRefetch: showMonitor && hasQueued && !hasShownCompletionMessage,
    });

    // Find parts without images
    const partInteractionsWithoutImages = partInteractions.filter(
        (partInteraction) =>
            !partInteraction.part_image && !partInteraction.defaultImageId,
    );

    // Track initial parts that need images
    useEffect(() => {
        if (
            partInteractionsWithoutImages.length === partInteractions.length &&
            partInteractions.length > 0 &&
            initialPartsNeedingImages.length === 0
        ) {
            setInitialPartsNeedingImages(
                partInteractionsWithoutImages.map((p) => p.id),
            );
        }
    }, [
        partInteractionsWithoutImages,
        partInteractions,
        initialPartsNeedingImages.length,
    ]);

    // Check if all initially tracked parts now have images
    const allInitialPartsHaveImages =
        initialPartsNeedingImages.length > 0 &&
        initialPartsNeedingImages.every((partId) => {
            const partInteraction = partInteractions.find(
                (p) => p.id === partId,
            );
            return (
                partInteraction &&
                (partInteraction.part_image ||
                    partInteraction.defaultImageId) &&
                partInteraction.part_image?.status === "COMPLETED"
            );
        });

    // Check generation status
    const counts = (
        batchStatus as {
            counts: { queued: number; inProgress: number; error: number };
        }
    )?.counts;
    const active = (counts?.queued ?? 0) + (counts?.inProgress ?? 0);
    const hasErrors = (counts?.error ?? 0) > 0;

    // AIDEV-NOTE: Fallback completion signal, time-based rather than poll-counting.
    // A counter is unsafe here: a status GET that STARTED before the enqueue but
    // resolved after it still carries a newer dataUpdatedAt, so it describes the
    // pre-enqueue world while looking current, and could push a counter over its
    // threshold. Requiring the reading itself to be well clear of the enqueue is
    // immune to that straddle — a late-resolving stale response lands only
    // milliseconds after queuedAt and cannot satisfy the window. Polls continue
    // every 5s via forceRefetch, so this is re-evaluated until it holds.
    const IDLE_SETTLE_MS = 10_000;
    const idleLongEnoughAfterQueue =
        statusTrusted &&
        queuedAt !== null &&
        !!dataUpdatedAt &&
        dataUpdatedAt - queuedAt >= IDLE_SETTLE_MS;

    // When we see any active work, mark that we've entered processing phase
    useEffect(() => {
        if (showMonitor && active > 0 && !hasSeenActive) {
            setHasSeenActive(true);
        }
    }, [showMonitor, active, hasSeenActive]);

    // AIDEV-NOTE: Handle completion when generation finishes
    useEffect(() => {
        // AIDEV-NOTE: Completion now defined as: queued -> saw active -> active === 0
        // AIDEV-NOTE: Three independent ways to conclude the batch is done. Relying
        // on hasSeenActive alone lost fast batches (see the settle-window note above);
        // allInitialPartsHaveImages is the strongest signal — the images we were
        // waiting for actually exist.
        const finishedSequence =
            showMonitor &&
            hasQueued &&
            active === 0 &&
            (hasSeenActive ||
                allInitialPartsHaveImages ||
                idleLongEnoughAfterQueue);

        if (finishedSequence && !hasShownCompletionMessage) {
            setHasShownCompletionMessage(true);

            const errorCount = counts?.error ?? 0;

            if (errorCount > 0) {
                toast.error(`Failed to generate ${errorCount} image(s)`);
            } else {
                toast.success(`Image generation complete`);
            }

            // Invoke completion callback once the sequence finishes
            onComplete?.();

            // Hide monitor after showing completion
            setTimeout(() => {
                setShowMonitor(false);
            }, 3000);
        }
    }, [
        showMonitor,
        active,
        hasQueued,
        hasSeenActive,
        allInitialPartsHaveImages,
        idleLongEnoughAfterQueue,
        hasShownCompletionMessage,
        counts,
        onComplete,
    ]);

    // AIDEV-NOTE: Retry uses batch API for failed images
    const handleRetryFailedImages = useCallback(async () => {
        if (!preferences) return;

        setShowMonitor(true);
        setHasShownCompletionMessage(false);
        setHasQueued(false);
        setQueuedAt(null);
        setStatusTrusted(false);
        setHasSeenActive(false);

        // Get parts that still don't have images
        const partsToRetry = partInteractionsWithoutImages.filter((p) => {
            const partInteraction = partInteractions.find(
                (pi) => pi.id === p.id,
            );
            return (
                !partInteraction?.part_image ||
                partInteraction.part_image.status === "ERROR"
            );
        });

        batchGenerateImages(
            {
                partIds: partsToRetry.map((p) => p.id),
                sessionId,
                clientId,
                scope: "part-ids",
            },
            {
                onSuccess: (response) => {
                    setHasQueued(
                        Boolean(
                            response.queued &&
                                (response.totalRequested ?? 0) > 0,
                        ),
                    );
                    setQueuedAt(Date.now());
                    // Discard whatever is cached or in flight and start a fetch we
                    // know began after the enqueue; only then trust the counts.
                    setStatusTrusted(false);
                    // cancelRefetch aborts any in-flight pre-enqueue request rather
                    // than letting refetch() dedupe onto its promise — otherwise the
                    // trust flag could be set by a response that started before this
                    // batch existed.
                    void refetchBatchStatus({ cancelRefetch: true }).finally(
                        () => setStatusTrusted(true),
                    );
                    toast.info("Retrying failed image generation...");
                },
                onError: (error) => {
                    logger
                        .withError(
                            error instanceof Error
                                ? error
                                : new Error(String(error)),
                        )
                        .error("Failed to retry batch image generation");
                    toast.error("Failed to retry image generation");
                    setShowMonitor(false);
                },
            },
        );
    }, [
        partInteractionsWithoutImages,
        partInteractions,
        preferences,
        batchGenerateImages,
        sessionId,
        clientId,
    ]);

    const handleGenerateImages = useCallback(async () => {
        if (!preferences) {
            toast.error("Unable to load image preferences");
            return;
        }

        if (
            !preferences.appearanceNotes ||
            preferences.appearanceNotes.trim().length === 0
        ) {
            toast.error("Please set up your part image preferences first.");
            return;
        }

        // Set monitor visible BEFORE starting the mutation
        setShowMonitor(true);
        setHasShownCompletionMessage(false);
        setHasQueued(false);
        setQueuedAt(null);
        setStatusTrusted(false);
        setHasSeenActive(false);

        // AIDEV-NOTE: Now using batch API for better performance
        const partIds = partInteractionsWithoutImages.map(
            (partInteraction) => partInteraction.id,
        );

        batchGenerateImages(
            {
                partIds,
                sessionId,
                clientId,
                scope: "part-ids",
            },
            {
                onSuccess: (response) => {
                    // Keep monitor visible to show progress
                    if (
                        response.totalRequested &&
                        response.totalRequested > 0
                    ) {
                        toast.info(
                            `Queued ${response.totalRequested} images for generation`,
                        );
                    }
                    setHasQueued(
                        Boolean(
                            response.queued &&
                                (response.totalRequested ?? 0) > 0,
                        ),
                    );
                    setQueuedAt(Date.now());
                    // Discard whatever is cached or in flight and start a fetch we
                    // know began after the enqueue; only then trust the counts.
                    setStatusTrusted(false);
                    void refetchBatchStatus().finally(() =>
                        setStatusTrusted(true),
                    );
                    // Don't hide monitor here - let the polling handle completion
                },
                onError: (error) => {
                    logger
                        .withError(
                            error instanceof Error
                                ? error
                                : new Error(String(error)),
                        )
                        .error("Failed to batch generate images");
                    toast.error("Failed to generate images");
                    setShowMonitor(false);
                },
            },
        );
    }, [
        partInteractionsWithoutImages,
        preferences,
        batchGenerateImages,
        sessionId,
        clientId,
    ]);

    if (!enabled) {
        return <></>;
    }

    // Show generate images prompt OR monitor (not both)
    if (
        !preferencesLoading &&
        preferences &&
        initialPartsNeedingImages.length > 0 &&
        !allInitialPartsHaveImages
    ) {
        // If we're generating or monitoring, show the monitor instead
        if (showMonitor) {
            // Show loading state while waiting for first poll result
            const isInitialLoading = !batchStatus;

            const isCompletePhase =
                hasQueued &&
                active === 0 &&
                (hasSeenActive ||
                    allInitialPartsHaveImages ||
                    idleLongEnoughAfterQueue);

            return (
                <Alert className="mb-6 border-blue-200 bg-blue-50">
                    <AlertDescription className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            {isInitialLoading || active ? (
                                <Loader2 className="size-4 animate-spin" />
                            ) : isCompletePhase ? (
                                <div className="size-4 rounded-full bg-green-500" />
                            ) : (
                                <Loader2 className="size-4 animate-pulse opacity-50" />
                            )}
                            <span className="font-medium text-blue-900">
                                {isInitialLoading
                                    ? "Starting image generation..."
                                    : active
                                      ? "Generating part images..."
                                      : isCompletePhase
                                        ? "Generation complete"
                                        : "Preparing batch generation..."}
                            </span>
                        </div>
                        {!isInitialLoading && (
                            <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-xs md:gap-3">
                                <span>Active: {active ?? 0}</span>
                                {(counts?.error ?? 0) > 0 && (
                                    <span className="text-destructive">
                                        Errors: {counts?.error ?? 0}
                                    </span>
                                )}
                            </div>
                        )}
                    </AlertDescription>
                </Alert>
            );
        }

        // Show the initial prompt with button
        return (
            <Alert className="mb-6 border-brand-terra-200 bg-brand-cream-100">
                <AlertDescription className="flex items-center justify-between">
                    <span className="text-brand-forest-800">
                        Your session reports now include images of your parts!
                        Would you like them created for this session?
                    </span>
                    <Button
                        variant="default"
                        size="sm"
                        onClick={handleGenerateImages}
                        disabled={isBatchGenerating}
                        className="ml-4"
                    >
                        Create Images
                    </Button>
                </AlertDescription>
            </Alert>
        );
    }

    // Show retry prompt for errors
    if (!showMonitor && hasErrors && hasShownCompletionMessage) {
        return (
            <Alert className="mb-6 border-red-200 bg-red-50">
                <ImageIcon className="size-4 text-red-600" />
                <AlertDescription className="flex items-center justify-between">
                    <span className="text-red-900">
                        {counts?.error ?? 0} image(s) failed to generate. Would
                        you like to retry?
                    </span>
                    <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleRetryFailedImages}
                        className="ml-4"
                    >
                        Retry failed
                    </Button>
                </AlertDescription>
            </Alert>
        );
    }

    return null;
};
